/* eslint-disable max-lines -- a single flat per-ChatItem dispatcher; each case is small and reads top-to-bottom, and splitting the switch across files would scatter one cohesive routing layer for no readability gain */
import { type ComponentType, Fragment, type ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

import type {
  ApprovalResolution,
  ApprovalVerdict,
  ChatItem,
  ToolUseItem,
  UserMessageItem,
} from '../../../contracts/harness.types';
import { isNonActionableToolError } from '../../../contracts/isNonActionableToolError';
import type { DesktopControlLeaseSnapshot } from '../../../contracts/projects/desktop-control-approval.types';
import type { ViewedDocumentContext } from '../../../contracts/projects/requests.types';
import { readVerifySubagentInput } from '../../../contracts/projects/VerifySubagentInlineBlock.utils';
import { readToolResultText } from '../../../contracts/toolResult';
import {
  nextUnansweredQuestion,
  parseQuestions,
  partitionClarificationQuestions,
} from '../../../data/harness/ask-user-details';
import type { Artifact } from '../../../data/projects/artifacts/state';
import {
  friendlyToolError,
  parseUserPreamble,
} from '../../../data/projects/components/projectChatMessage.helpers';
import type { LiveDelegateStep } from '../../../data/projects/hooks/useProjectDesktopDelegation';
import {
  isConnectWaitItem,
  readConnectRequired,
  turnHasConnectRequiredBefore,
} from '../../../data/projects/integrations/connectRequired';
import {
  renderSourcesReferencesForAssistant,
  type SourceGroundedReference,
} from '../../../data/projects/source-grounded-citations';
import type { ClarificationAnswerDrafts } from '../../../data/projects/state/clarificationAnswerDraftAtom';
import {
  isSuppressedTodoToolUse,
  type LatestTodoPlan,
} from '../../../data/projects/timeline/agent-todo/computeLatestTodoPlan';
import { resolveTodoToolUse } from '../../../data/projects/timeline/agent-todo/resolveTodoToolUse';
import type { AgentTodoItem } from '../../../data/projects/timeline/agent-todo/types';
import {
  extractResolvedClarifications,
  isResolvedClarification,
  type ResolvedClarification,
} from '../../../data/projects/timeline/clarificationAnswers';
import { deniedApprovalUserNote } from '../../../data/projects/timeline/deniedApprovalUserNote';
import { toBlockStatus } from '../../../data/projects/timeline/genericToolUseStatus';
import {
  objectKindForTool,
  type WrittenObject,
} from '../../../data/projects/timeline/object-card/objectWrites';
import {
  resolveToolPresentation,
  type ToolCardKind,
} from '../../../data/projects/timeline/presentation/tool-registry';
import type {
  ToolUseGroupDecision,
  WorkingBlockDecision,
} from '../../../data/projects/timeline/presentation/types';
import {
  type AgentTimelineProps,
  TurnFailedStatusCard,
} from '../../agent-harness/components/agent-timeline/agent-timeline';
import type { ComputerUseToolUseHost } from '../components/ComputerUseTaskBlock.host';
import type { ConnectRequiredBlockProps } from '../components/ConnectRequiredBlock';
import { VerifyChildRunInline } from '../components/verify-subagent/VerifyChildRunInline';
import { TodoToolUseBlock } from './agent-todo/TodoToolUseBlock';
import { GenericToolUseRow } from './GenericToolUseRow';
import { GroupedToolUseBlock } from './GroupedToolUseBlock';
import { InlineToolCallRow } from './InlineToolCallRow';
import {
  renderObjectCreationBulk,
  renderObjectCreationToolUse,
} from './object-card/renderObjectCreationToolUse';
import { renderChildRunToolUse } from './renderChildRunToolUse';
import { renderComputerUseToolUse } from './renderComputerUseToolUse';
import { renderGenerateConsultNoteToolUse } from './renderGenerateConsultNoteToolUse';
import {
  type InlineApprovalRendererHost,
  renderInlineApprovalToolUse,
} from './renderInlineApprovalToolUse';
import { renderVerifyToolUse } from './renderVerifyToolUse';
import { renderWorkflowToolUse } from './renderWorkflowToolUse';
import type { EvidenceTrails } from './working-block/evidence/evidenceTrail';
import { WorkingBlock } from './working-block/WorkingBlock';

const EMPTY_SOURCE_REFERENCES: SourceGroundedReference[] = [];

type AgentTimelineItemContext = Parameters<
  NonNullable<AgentTimelineProps['renderItem']>
>[2];

export interface ProjectItemRendererDeps {
  /** Top-level timeline items; used only for turn-local rendering context. */
  items?: ChatItem[];
  /** Precomputed predecessor lookup for render_sources citation association. */
  previousItemsById?: ReadonlyMap<string, ChatItem>;
  /** Enables the compact, actionable Work source-citation chip rollout. */
  isWorkSourceCitationChipsEnabled?: boolean;
  /** Durable run id — scopes attachment download URLs in the user bubble. */
  agentRunId: string;
  /**
   * Project id — passed through to ``ApprovalRequestBlock`` so it can POST
   * a verdict via the projects router. Required for any non-CUA HITL
   * approval that lands inline in the transcript.
   */
  projectId: string;
  /** Mount the artifact panel beside the chat (writes the open-artifact atom). */
  onOpenArtifact: (artifact: Artifact) => void;
  /**
   * Resolve a harness approval from an inline project block. Returns
   * a Promise that rejects on BE failure so the inline card can clear
   * its pending-verdict latch and let the user retry. On success the
   * promise resolves; the card keeps the latch set until the SSE
   * refetch unmounts it (re-enabling the buttons in the BE-OK-but-
   * SSE-not-yet gap would let a fast click 409 against the
   * already-resolved gate).
   */
  onResolveApproval?: (
    approvalId: string,
    verdict: ApprovalVerdict,
    reason?: string,
    modifiedInput?: Record<string, unknown> | null,
    resolution?: ApprovalResolution
  ) => Promise<void>;
  /** True while approval resolution is in flight. */
  resolvingApproval?: boolean;
  /** Quiet desktop permission preflight before approving computer-use tasks. */
  requireComputerUsePermissions?: () => Promise<boolean>;
  /** Live step for the currently-active delegated desktop task, if any. */
  liveDelegateStep?: LiveDelegateStep;
  /** Current desktop-control lease snapshot; passed to make blocker rendering reactive. */
  desktopControlLease?: DesktopControlLeaseSnapshot | null;
  /** Native target mode owns its executor lease after approval. */
  desktopTargetEnabled?: boolean;
  /**
   * Latest known status per todo id from the full timeline — forward-fills
   * completed/cancelled on older todo blocks so they don't keep spinners.
   */
  latestTodoItemsById?: ReadonlyMap<number, AgentTodoItem>;
  /**
   * Current plan derived from `todo_state` results. When set, todo ops render
   * as one update-in-place plan card at the anchor instead of per-op rows.
   */
  latestTodoPlan?: LatestTodoPlan | null;
  /**
   * Per-item grouping verdicts (keyed by `ChatItem.id`) that fold runs of
   * identical back-to-back tool calls into one collapsible summary. Items
   * absent from the map render as a single row, as before.
   */
  toolGroups?: Map<string, ToolUseGroupDecision>;
  /** Precomputed contiguous bulk-creation runs, keyed by tool-call id. */
  creationBatches?: ReadonlyMap<string, BulkCreationBatch>;
  /**
   * Per-item verdicts (keyed by `ChatItem.id`) folding runs of consecutive
   * non-answer items into one collapsible Working block. Items absent from the
   * map render on their own, outside any block.
   */
  workingBlocks?: Map<string, WorkingBlockDecision>;
  /** True while the turn is still running — makes the Working block header live. */
  turnInFlight?: boolean;
  /** Whether this renderer is allowed to mount inline child run lifecycles. */
  allowChildStreams: boolean;
  /** Gates custom Nango connector HITL cards (generic Approve/Cancel remains). */
  isNangoIntegrationsEnabled?: boolean;
  /** Latest parent rows for reused persistent child runs. */
  latestPersistentChildToolCallIds?: ReadonlySet<string>;
  /** Localizes the Evidence artefact-adapter title fallbacks. */
  intl: IntlShape;
  shouldAnimateUserMessage?: (item: UserMessageItem) => boolean;
  /**
   * Project title — used to label the first user turn when it is a legacy
   * instructed workflow seed (full agent brief persisted as the user message).
   */
  projectTitle?: string;
  /** The chat's routine, when it came from a run; the ``Run {name}`` seed carries no workflow id. */
  routineRunWorkflowId?: string;
  /** Stashed folded-text / mixed-card answers, keyed by approval id. */
  clarificationAnswerDrafts?: ClarificationAnswerDrafts;
  /** Current visible document body used to expand span edits into a full draft. */
  viewedDocument?: ViewedDocumentContext & { session_id?: string };
  /** Reply actions routed to Evidence child tool calls, keyed by tool-call id. */
  inlineReplyActions?: ReadonlyMap<string, (copyableText: string | null) => ReactNode>;
  /** The latest turn's contextual loader phrases; a live Working block with nothing to name rotates them. */
  loaderPhrases?: readonly string[];
  /**
   * The Working block's Evidence port: each Evidence step's trail, read from
   * Evidence's sessions and child runs, and the opener for its sources. The app
   * binds it once per renderer; without it a Working row names no sources.
   */
  evidenceTrails?: EvidenceTrails;
}

/** The user's turn as the timeline draws it. */
export interface ProjectTimelineUserBubbleProps {
  text: string;
  attachments: UserMessageItem['attachments'];
  agentRunId: string;
  animate?: boolean;
  /** The routine this message was sent with, when its seed is a message of its own. */
  launchedRoutine?: { title: string; workflowId?: string };
  /** Project title — labels legacy workflow-run seeds that still persist the agent brief. */
  routineRunTitleFallback?: string;
  /** True only for the chat's opening user message. */
  isRoutineRunSeed?: boolean;
  isRoutineRunning?: boolean;
  /** Fallback for a ``Run {name}`` seed, which carries no ``workflow_id``. */
  routineRunWorkflowId?: string;
  onOpenRoutine?: (workflowId: string) => void;
}

/**
 * What the dispatcher draws with and reaches through that the app owns: the
 * timeline's leaves, Evidence's cards, the connect card, the chat object list,
 * the artifact deriver, the two approval hosts and a child run's cancel. One
 * module-level object at the composition point, so every member keeps one
 * identity.
 */
export interface ProjectItemRendererHost {
  UserBubble: ComponentType<ProjectTimelineUserBubbleProps>;
  AssistantText: ComponentType<{
    text: string;
    streaming: boolean;
    structuredReferences?: SourceGroundedReference[];
  }>;
  ThinkingText: ComponentType<{ text: string }>;
  ErrorRow: ComponentType<{ message: string }>;
  TurnStoppedNote: ComponentType<{ label: string }>;
  ResolvedClarificationsCard: ComponentType<{ clarifications: ResolvedClarification[] }>;
  /**
   * The parent-facing answer with the citation and image tokens it copied from
   * a child degraded away, so a raw token never leaks: the child's own inline
   * answer is the authoritative one.
   */
  stripAnswerTokens: (text: string, options: { streaming: boolean }) => string;
  /** Settled prose, for a Verify analysis's findings. */
  renderSettledAssistantText: (text: string) => ReactNode;
  /** Prose that may still be streaming, for the Verify child timeline. */
  renderAssistantText: (text: string, streaming: boolean) => ReactNode;
  /** The connect card, bound to the integrations connect buttons. */
  renderConnectRequired: (props: ConnectRequiredBlockProps) => ReactNode;
  /** The chat object list a bulk create renders as. */
  renderChatObjectList: (objects: WrittenObject[]) => ReactNode;
  /** Derives the artifact a tool call opens; the app binds the slide-builder flag and session tabs. */
  resolveArtifact: (item: ToolUseItem) => Artifact | undefined;
  /** Evidence's card for a tool its adapters own. */
  renderEvidenceCard: (item: ToolUseItem, deps: ProjectItemRendererDeps) => ReactNode;
  /** Evidence's sub-agent card: the child run it streams, or the bridged session. */
  renderEvidenceSubagent: (item: ToolUseItem, deps: ProjectItemRendererDeps) => ReactNode;
  inlineApproval: InlineApprovalRendererHost;
  computerUse: ComputerUseToolUseHost;
  /** Cancels a child run's turn on the app's client; rejects when the cancel fails. */
  cancelChildRun: (childAgentRunId: string) => Promise<void>;
}

/**
 * A Work home routine launch stores the routine's seed and the clinician's
 * words as two user messages in the opening turn. Show them as one bubble:
 * the routine chip followed by what the clinician typed.
 */
const findRoutineLaunchPair = (
  items: readonly ChatItem[],
  projectTitle: string | undefined,
  routineRunWorkflowId: string | undefined
):
  | { seedId: string; messageId: string; routine: { title: string; workflowId?: string } }
  | undefined => {
  const users = items.filter(
    (entry): entry is UserMessageItem => entry.kind === 'user_message'
  );
  const [seed, message] = users;
  if (!seed || !message || !seed.turnId || seed.turnId !== message.turnId)
    return undefined;
  const parsed = parseUserPreamble(seed.text, {
    routineRunTitleFallback: projectTitle,
    isRoutineRunSeed: true,
  });
  if (!parsed.routineRunTitle || parsed.body) return undefined;
  return {
    seedId: seed.id,
    messageId: message.id,
    routine: {
      title: parsed.routineRunTitle,
      workflowId:
        seed.text.match(/workflow_id:\s*([\w-]+)/i)?.[1] ?? routineRunWorkflowId,
    },
  };
};

/**
 * Build the `renderItem` render-prop for `<AgentTimeline />`.
 *
 * Routes each harness `ChatItem` to its Evidence-styled presentation:
 * assistant markdown (+ chain-marker pills) and the user bubble come from
 * `projectTimelineBlocks`; `tool_use` dispatches to the project leaf
 * blocks (computer-use task card, Evidence sub-agent inline view,
 * resolved-clarification summary, artifact "Open", or a generic tool
 * row). The harness owns the timeline + data; this owns the look.
 *
 * A pending `kind=text` `ask_user` wait folds into the transcript as
 * assistant prose (one unanswered question at a time). Structured kinds
 * stay in the composer dock via `InlineClarificationBlock`. A computer-use
 * approval renders its underlying task block in a waiting state with inline
 * Approve/Deny controls when the pending approval id is available.
 */
export const createProjectItemRenderer = (
  deps: ProjectItemRendererDeps,
  host: ProjectItemRendererHost
) => {
  const { UserBubble, AssistantText, ThinkingText, ErrorRow, TurnStoppedNote } = host;
  const evidenceTrails = deps.evidenceTrails;
  // `renderItem` is called once per timeline node. Scan contiguous create runs
  // once per renderer, rather than searching the full timeline per node.
  const creationBatches =
    deps.creationBatches ?? computeBulkCreationBatches(deps.items ?? []);
  const renderingDeps: ProjectItemRendererDeps = {
    ...deps,
    creationBatches,
  };
  const firstUserMessageId = deps.items?.find(
    (entry): entry is UserMessageItem => entry.kind === 'user_message'
  )?.id;
  const routineLaunch = findRoutineLaunchPair(
    deps.items ?? [],
    deps.projectTitle,
    deps.routineRunWorkflowId
  );
  let lastUserMessageId: string | undefined;
  for (const entry of deps.items ?? []) {
    if (entry.kind === 'user_message') lastUserMessageId = entry.id;
  }

  // No knowledge of Working-block grouping, so the block can reuse it for its
  // members without recursing. `projection` splits a tool into the row inside
  // the Working block and the optional card sibling beneath it.
  const renderLeaf = (
    item: ChatItem,
    context: AgentTimelineItemContext = { turnFailed: false },
    projection?: ToolProjection
  ): ReactNode => {
    switch (item.kind) {
      case 'user_message':
        if (item.id === routineLaunch?.seedId) return null;
        return (
          <UserBubble
            launchedRoutine={
              item.id === routineLaunch?.messageId ? routineLaunch.routine : undefined
            }
            text={item.text}
            attachments={item.attachments}
            agentRunId={deps.agentRunId}
            animate={deps.shouldAnimateUserMessage?.(item) ?? true}
            routineRunTitleFallback={
              item.id === firstUserMessageId ? deps.projectTitle : undefined
            }
            isRoutineRunSeed={item.id === firstUserMessageId}
            routineRunWorkflowId={
              item.id === firstUserMessageId ? deps.routineRunWorkflowId : undefined
            }
            isRoutineRunning={Boolean(deps.turnInFlight && item.id === lastUserMessageId)}
            onOpenRoutine={(workflowId) =>
              deps.onOpenArtifact({ type: 'workflow', workflowId, openInEdit: true })
            }
          />
        );
      case 'assistant_text':
        // Parent-facing answer: the inline child response is authoritative, so any
        // `<cite>`/`<img>` tokens the parent copied degrade away rather than leaking a raw token.
        if (turnHasConnectRequiredBefore(deps.items, item.id)) return null;
        return (
          <AssistantText
            text={host.stripAnswerTokens(item.text, { streaming: item.streaming })}
            streaming={item.streaming}
            structuredReferences={
              deps.isWorkSourceCitationChipsEnabled
                ? renderSourcesReferencesForAssistant(deps.previousItemsById, item)
                : EMPTY_SOURCE_REFERENCES
            }
          />
        );
      case 'thinking':
        return <ThinkingText text={item.text} />;
      case 'tool_use': {
        const block = renderToolUse(item, renderingDeps, host, context, projection);
        // The backend writes no user turn for the note, so anchor the bubble
        // under the card it spoke to — never inside the collapsible row.
        const userNote = deniedApprovalUserNote(item);
        if (!userNote || projection?.kind === 'row') return block;
        return (
          <>
            {block}
            <UserBubble
              text={userNote}
              attachments={[]}
              agentRunId={deps.agentRunId}
              animate={false}
            />
          </>
        );
      }
      case 'prompt_context_failed':
        return (
          <ErrorRow
            message={
              item.message ??
              deps.intl.formatMessage({
                defaultMessage: 'Prompt context failed.',
                id: 'dXQASznAl0',
                description:
                  'Fallback line when prompt context loading fails without detail',
              })
            }
          />
        );
      case 'turn_cancelled_marker':
        // Stopped mid-work. A pre-output stop never reaches here — the timeline
        // builder drops the bubble + marker for it (`dropEmptyCancelledTurns`)
        // and the composer restores the prompt instead.
        return (
          <TurnStoppedNote
            label={deps.intl.formatMessage({
              defaultMessage:
                'Stopped. Send a new message to tell Heidi what to do next.',
              id: 'fR/C7kSDeF',
            })}
          />
        );
      case 'turn_failed':
        return <TurnFailedStatusCard reason={item.reason} />;
      default: {
        // Exhaustiveness guard: a new ChatItem kind fails type-check here
        // rather than silently rendering nothing.
        const _exhaustive: never = item;
        void _exhaustive;
        return null;
      }
    }
  };

  // This is a render callback for AgentTimeline's `renderItem` prop, not a
  // component — it has no own state/identity, so a display name is moot.
  // eslint-disable-next-line react/display-name
  return (
    item: ChatItem,
    _defaultRender?: (item: ChatItem) => ReactNode,
    context: AgentTimelineItemContext = { turnFailed: false }
  ): ReactNode => {
    // A `member` is already drawn by its block's `start`.
    const blockDecision = deps.workingBlocks?.get(item.id);
    if (blockDecision?.role === 'member') return null;
    if (blockDecision?.role === 'start') {
      // Cards are built once here: they render beneath the block (before the
      // next text), and each row learns from this map whether its card exists.
      const cardById = new Map<string, ReactNode>();
      for (const member of blockDecision.members) {
        if (member.kind !== 'tool_use') continue;
        const card = renderLeaf(member, context, CARD_PROJECTION);
        if (card != null) cardById.set(member.id, card);
      }
      return (
        <div className="flex min-w-0 flex-col gap-3">
          <WorkingBlock
            members={blockDecision.members}
            turnInFlight={deps.turnInFlight ?? false}
            openEnded={blockDecision.openEnded}
            liveSpanMs={blockDecision.liveSpanMs}
            estimatedSpan={blockDecision.estimatedSpan}
            rows={blockDecision.rows}
            closedBy={blockDecision.closedBy}
            evidence={evidenceTrails}
            phrases={deps.loaderPhrases}
            renderMember={(member) =>
              renderLeaf(member, context, {
                kind: 'row',
                hasCard: cardById.has(member.id),
              })
            }
          />
          {[...cardById].map(([id, card]) => (
            <Fragment key={id}>{card}</Fragment>
          ))}
        </div>
      );
    }

    return renderLeaf(item, context);
  };
};

/** The `renderItem` the dispatcher builds for `<AgentTimeline />`. */
export type ProjectItemRenderer = ReturnType<typeof createProjectItemRenderer>;

/**
 * A tool renders as a row inside the Working block and, if it opted in, a card
 * beneath it. The row needs to know whether its card exists (a card owns the
 * Open affordance and the failure detail), so the section builds cards first.
 */
type ToolProjection =
  | { kind: 'card' }
  | {
      kind: 'row';
      hasCard: boolean;
    };

const CARD_PROJECTION: ToolProjection = { kind: 'card' };

type CardRenderer = (
  item: ToolUseItem,
  deps: ProjectItemRendererDeps,
  host: ProjectItemRendererHost,
  context: AgentTimelineItemContext,
  labelledByRow?: boolean
) => ReactNode;

// One function for the kinds Evidence's adapters own, so a card decision can
// tell them apart by identity.
const renderEvidenceCard: CardRenderer = (item, deps, host) =>
  host.renderEvidenceCard(item, deps);

const CARD_RENDERERS: Record<ToolCardKind, CardRenderer> = {
  object: (item, deps, host) => {
    // Calls observed before execution must not flash individual object cards
    // before the parallel create batch can be rendered as one bulk card.
    if (
      deps.turnInFlight &&
      (item.status === 'streaming_input' || item.status === 'queued')
    ) {
      return null;
    }
    return renderObjectCreationToolUse(item, {
      onOpenArtifact: deps.onOpenArtifact,
      intl: deps.intl,
      items: deps.items,
      resolveArtifact: host.resolveArtifact,
    });
  },
  routine: (item, deps, host) =>
    renderWorkflowToolUse(item, deps.onOpenArtifact, host.resolveArtifact),
  'evidence-subagent': (item, deps, host) => host.renderEvidenceSubagent(item, deps),
  'verify-subagent': (item, deps, host) => {
    if (!item.childAgentRunId) return null;
    const input = readVerifySubagentInput(item.input);
    return (
      <VerifyChildRunInline
        childAgentRunId={item.childAgentRunId}
        status={toBlockStatus(item.status)}
        question={input.question}
        allowMount={deps.allowChildStreams}
        renderAssistantText={host.renderAssistantText}
      />
    );
  },
  'computer-use': (item, deps, host, context, labelledByRow) =>
    renderComputerUseToolUse(
      item,
      {
        ...deps,
        previousUserText: previousUserMessageText(deps.items, item),
        turnFailed: context.turnFailed,
      },
      toBlockStatus(item.status),
      item.status === 'pending_approval',
      host.computerUse,
      { labelledByRow }
    ),
  'child-run': (item, deps, host) =>
    renderChildRunToolUse(
      item,
      deps,
      toBlockStatus(item.status),
      (childDeps) => createProjectItemRenderer(childDeps, host),
      host.cancelChildRun
    ),
  'consult-note': (item, deps, host) =>
    renderGenerateConsultNoteToolUse(item, {
      onOpenArtifact: deps.onOpenArtifact,
      intl: deps.intl,
      resolveArtifact: host.resolveArtifact,
      renderConnectRequired: host.renderConnectRequired,
    }),
  'verify-analysis': (item, _deps, host) =>
    renderVerifyToolUse(
      item,
      toBlockStatus(item.status),
      host.renderSettledAssistantText
    ),
  'export-pdf': renderEvidenceCard,
  'export-csv': renderEvidenceCard,
  'export-excel': renderEvidenceCard,
  'export-pptx': renderEvidenceCard,
  'export-word': renderEvidenceCard,
  'export-to-word': renderEvidenceCard,
  'patient-evidence-pdf': renderEvidenceCard,
  'fill-pdf-form': renderEvidenceCard,
  'coverage-eligibility': renderEvidenceCard,
  'multiplayer-handoff': renderEvidenceCard,
  'clinical-task': renderEvidenceCard,
  'medical-calculator': renderEvidenceCard,
  'create-template': renderEvidenceCard,
  'open-template-editor': renderEvidenceCard,
  'pin-template': renderEvidenceCard,
  'unpin-template': renderEvidenceCard,
  'save-work-output': renderEvidenceCard,
  'record-note-push-hints': renderEvidenceCard,
  'report-issue': renderEvidenceCard,
  'update-word-library': renderEvidenceCard,
  'session-recording': renderEvidenceCard,
};

const renderCardContent = (
  item: ToolUseItem,
  deps: ProjectItemRendererDeps,
  host: ProjectItemRendererHost,
  context: AgentTimelineItemContext,
  labelledByRow: boolean
): ReactNode => {
  const card = resolveToolPresentation(item).card;
  if (!card) return null;
  const skipEvidenceAdapter =
    item.status === 'pending_approval' &&
    (item.approval?.approval_kind === 'clinical_task' || isConnectWaitItem(item));
  if (skipEvidenceAdapter && CARD_RENDERERS[card] === renderEvidenceCard) return null;
  return CARD_RENDERERS[card](item, deps, host, context, labelledByRow);
};

/** The one failure the clinician can act on: connect an app. */
const readRecovery = (item: ToolUseItem): ReturnType<typeof readConnectRequired> =>
  toBlockStatus(item.status) === 'error' ? readConnectRequired(item) : null;

const hasRecoveryAction = (item: ToolUseItem): boolean => readRecovery(item) !== null;

/**
 * A failure the clinician can act on renders its call to action as the tool's
 * card — beneath the Working block, visible without opening it, and left in
 * place when the block collapses. Its row inside the block stays a plain failed row.
 */
const renderRecoveryCard = (
  item: ToolUseItem,
  deps: ProjectItemRendererDeps,
  host: ProjectItemRendererHost
): ReactNode => {
  const payload = readRecovery(item);
  if (!payload) return null;
  return host.renderConnectRequired({
    payload,
    error: friendlyToolError(readToolResultText(item)),
    toolCallId: item.id,
    items: deps.items,
  });
};

interface BulkCreationBatch {
  members: ToolUseItem[];
}

/** Only one-task clinical-task calls join a batch; a multi-task call approves as one. */
const batchableObjectCreationKind = (
  item: ToolUseItem
): NonNullable<ReturnType<typeof objectKindForTool>> | undefined => {
  const kind = objectKindForTool(item.name);
  if (!kind) return undefined;
  if (kind !== 'task') return kind;
  const tasks = item.input?.tasks;
  return Array.isArray(tasks) && tasks.length === 1 ? kind : undefined;
};

/**
 * Precompute contiguous same-kind create runs once per renderer. A tool-call
 * id maps to its run, so every projection (Working row, card and standalone)
 * reads the same batch without repeatedly scanning the complete timeline.
 */
const computeBulkCreationBatches = (
  items: ChatItem[]
): ReadonlyMap<string, BulkCreationBatch> => {
  const batches = new Map<string, BulkCreationBatch>();
  let index = 0;
  while (index < items.length) {
    const head = items[index];
    const kind =
      head?.kind === 'tool_use' ? batchableObjectCreationKind(head) : undefined;
    if (!head || head.kind !== 'tool_use' || !kind) {
      index += 1;
      continue;
    }

    let end = index + 1;
    while (true) {
      const candidate = items[end];
      if (
        candidate?.kind !== 'tool_use' ||
        batchableObjectCreationKind(candidate) !== kind
      ) {
        break;
      }
      end += 1;
    }

    const members = items.slice(index, end) as ToolUseItem[];
    if (members.length >= 2) {
      const batch = { members };
      for (const member of members) batches.set(member.id, batch);
    }
    index = end;
  }
  return batches;
};

/** While a batch awaits approval, only its first outstanding approval renders. */
const isLaterBatchApproval = (
  item: ToolUseItem,
  batches: ReadonlyMap<string, BulkCreationBatch> | undefined
): boolean => {
  if (item.status !== 'pending_approval') return false;
  const activeApproval = batches
    ?.get(item.id)
    ?.members.find((member) => member.status === 'pending_approval');
  return activeApproval !== undefined && activeApproval.id !== item.id;
};

const renderStandaloneInteraction = (
  item: ToolUseItem,
  deps: ProjectItemRendererDeps,
  host: ProjectItemRendererHost,
  context: AgentTimelineItemContext
): ReactNode => {
  const { AssistantText, UserBubble, ResolvedClarificationsCard } = host;
  const presentation = resolveToolPresentation(item);
  const awaitingApproval = item.status === 'pending_approval';

  // Clarification tools. Folded ``kind=text`` questions render in the
  // transcript as Heidi's question (plus prior composer replies from the
  // draft), but only while a text question is actually the EARLIEST
  // unanswered one in the agent's order — otherwise an unanswered
  // structured question earlier in the batch would be starved: the card
  // that alone can answer it lives in the composer dock (below), and
  // this transcript fold would keep surfacing a later text question
  // ahead of it forever. Structured kinds stay hidden here full stop —
  // their card lives in the composer dock. Once resolved, collapse the
  // run of consecutive resolved clarifications into one "Answers" card
  // rather than dumping the raw question + options JSON.
  if (presentation.interaction === 'clarification') {
    if (awaitingApproval || item.status === 'running') {
      const questions = parseQuestions(item.input);
      if (!questions) return null;
      const { text: textQuestions } = partitionClarificationQuestions(questions);
      if (textQuestions.length === 0) return null;
      const approvalId = item.pendingApprovalId ?? item.approval?.approval_id;
      const draft = (approvalId && deps.clarificationAnswerDrafts?.[approvalId]) || {};
      const earliestUnanswered = nextUnansweredQuestion(questions, draft);
      const next = earliestUnanswered?.kind === 'text' ? earliestUnanswered : undefined;
      const answered = textQuestions.filter((question) => {
        const answer = draft[question.id];
        return typeof answer?.text === 'string' && answer.text.length > 0;
      });
      if (answered.length === 0 && !next) return null;
      return (
        <>
          {answered.map((question) => (
            <Fragment key={question.id}>
              <AssistantText text={question.question} streaming={false} />
              <UserBubble
                text={draft[question.id]?.text ?? ''}
                attachments={[]}
                agentRunId={deps.agentRunId}
                animate={false}
              />
            </Fragment>
          ))}
          {next ? <AssistantText text={next.question} streaming={false} /> : null}
        </>
      );
    }
    const timeline = deps.items;
    const idx = timeline
      ? timeline.findIndex((candidate) => candidate.id === item.id)
      : -1;
    if (timeline && idx >= 0) {
      // A clarification preceded by another resolved one is a member of
      // the run; its card is drawn by the run's first item, so skip it.
      const previous = idx > 0 ? timeline[idx - 1] : undefined;
      if (previous && isResolvedClarification(previous)) return null;
      const members: ToolUseItem[] = [];
      for (let i = idx; i < timeline.length; i += 1) {
        const candidate = timeline[i];
        if (candidate && isResolvedClarification(candidate)) members.push(candidate);
        else break;
      }
      // ``item`` may not itself satisfy ``isResolvedClarification`` for an
      // edge status — fall back to rendering it alone so a resolved
      // question is never dropped from the transcript.
      const clarifications = (members.length > 0 ? members : [item]).flatMap(
        extractResolvedClarifications
      );
      return <ResolvedClarificationsCard clarifications={clarifications} />;
    }
    return (
      <ResolvedClarificationsCard clarifications={extractResolvedClarifications(item)} />
    );
  }

  // Computer-use owns its own Approve/Deny on the card — it must win over
  // the generic ApprovalRequestBlock.
  if (item.isComputerUse || presentation.card === 'computer-use') {
    const computerUse = renderCardContent(item, deps, host, context, false);
    if (computerUse != null) return computerUse;
  }

  // Folded into the plan card (or a deferred deny) — render nothing, and
  // don't let the item fall through to the generic row.
  if (isSuppressedTodoToolUse(item, deps.latestTodoPlan)) return null;

  // Avoid showing the same plan twice.
  if (isRedundantComputerUseTodoPlan(item, deps.items, deps.latestTodoPlan)) {
    return null;
  }

  const todoModel = resolveTodoToolUse(item, deps.intl, {
    latestTodoItemsById: deps.latestTodoItemsById,
    latestTodoPlan: deps.latestTodoPlan,
    projectTitle: deps.projectTitle,
  });
  if (todoModel)
    return <TodoToolUseBlock model={todoModel} turnFailed={context.turnFailed} />;

  if (isLaterBatchApproval(item, deps.creationBatches)) return null;
  const inlineApproval = renderInlineApprovalToolUse(item, deps, host.inlineApproval);
  if (inlineApproval) return inlineApproval;

  const card = renderCardContent(item, deps, host, context, false);
  if (card != null) return card;

  return (
    <GenericToolUseRow
      item={item}
      items={deps.items}
      onOpenArtifact={deps.onOpenArtifact}
      resolveArtifact={host.resolveArtifact}
      renderConnectRequired={host.renderConnectRequired}
    />
  );
};

const renderToolUse = (
  item: ToolUseItem,
  deps: ProjectItemRendererDeps,
  host: ProjectItemRendererHost,
  context: AgentTimelineItemContext,
  projection?: ToolProjection
): ReactNode => {
  // Before execution starts, a template call may still become a multi-item
  // approval batch. Keep its progress in the enclosing Working header rather
  // than rendering either a singular template card or a generic duplicate row.
  if (item.name === 'create_template' && item.status === 'queued') return null;

  if (
    toBlockStatus(item.status) === 'error' &&
    isNonActionableToolError(readToolResultText(item))
  ) {
    return null;
  }

  const presentation = resolveToolPresentation(item);
  if (projection === undefined && presentation.axis === 'interaction') {
    return renderStandaloneInteraction(item, deps, host, context);
  }

  const groupDecision = deps.toolGroups?.get(item.id);

  // A group's start renders every member's card, so its members render none of
  // their own. A call whose own card renders nothing (none registered, or one
  // that declines this call) shows its recovery action instead, beneath the
  // Working block whichever row it belongs to.
  if (projection?.kind === 'card') {
    if (groupDecision?.role === 'member') return null;
    if (groupDecision?.role === 'start') {
      return renderGroupCards(groupDecision.members, deps, host, context);
    }
    return (
      renderObjectCreationBulk([item], host.renderChatObjectList) ??
      renderCardContent(item, deps, host, context, true) ??
      renderRecoveryCard(item, deps, host)
    );
  }
  if (groupDecision?.role === 'member') return null;

  if (projection === undefined) {
    // Standalone activity (no Working-block map): row + optional card.
    const card = renderToolUse(item, deps, host, context, CARD_PROJECTION);
    const row = renderToolUse(item, deps, host, context, {
      kind: 'row',
      hasCard: card != null,
    });
    if (row == null || card == null) return row ?? card;
    return (
      <div className="flex min-w-0 flex-col gap-3">
        {row}
        {card}
      </div>
    );
  }

  if (groupDecision?.role === 'start') {
    // The grouped label names one tool, so a mixed run keeps its own rows.
    const [head] = groupDecision.members;
    if (groupDecision.members.some((member) => member.name !== head?.name)) {
      const ungrouped = { ...deps, toolGroups: undefined };
      const listsObjects =
        renderObjectCreationBulk(groupDecision.members, host.renderChatObjectList) !=
        null;
      return groupDecision.members.map((member) => (
        <Fragment key={member.id}>
          {renderToolUse(member, ungrouped, host, context, {
            kind: 'row',
            hasCard:
              listsObjects ||
              resolveToolPresentation(member).card !== undefined ||
              hasRecoveryAction(member),
          })}
        </Fragment>
      ));
    }
    return (
      <GroupedToolUseBlock
        members={groupDecision.members}
        renderMember={(member) =>
          hasRecoveryAction(member) ? (
            <InlineToolCallRow
              toolName={member.name}
              toolInput={member.input}
              status={toBlockStatus(member.status)}
              toolCallDisplay={member.toolCallDisplay}
            />
          ) : (
            <GenericToolUseRow
              item={member}
              items={deps.items}
              onOpenArtifact={deps.onOpenArtifact}
              resolveArtifact={host.resolveArtifact}
              renderConnectRequired={host.renderConnectRequired}
            />
          )
        }
      />
    );
  }
  // The card owns the Open affordance and the failure detail; a row without
  // one keeps the generic row's error/connect/desktop-required handling.
  if (projection.hasCard) {
    return (
      <InlineToolCallRow
        toolName={item.name}
        toolInput={item.input}
        status={toBlockStatus(item.status)}
        toolCallDisplay={item.toolCallDisplay}
      />
    );
  }
  return (
    <GenericToolUseRow
      item={item}
      items={deps.items}
      onOpenArtifact={deps.onOpenArtifact}
      resolveArtifact={host.resolveArtifact}
      renderConnectRequired={host.renderConnectRequired}
    />
  );
};

/** Two or more written objects render as one list; otherwise each member keeps its own card. */
const renderGroupCards = (
  members: ToolUseItem[],
  deps: ProjectItemRendererDeps,
  host: ProjectItemRendererHost,
  context: AgentTimelineItemContext
): ReactNode => {
  const list = renderObjectCreationBulk(members, host.renderChatObjectList);
  if (list) return list;
  const ungrouped = { ...deps, toolGroups: undefined };
  const cards = members.flatMap((member) => {
    const card = renderToolUse(member, ungrouped, host, context, CARD_PROJECTION);
    return card == null ? [] : [<Fragment key={member.id}>{card}</Fragment>];
  });
  return cards.length > 0 ? cards : null;
};

const isRedundantComputerUseTodoPlan = (
  item: ToolUseItem,
  items: readonly ChatItem[] | undefined,
  latestTodoPlan: LatestTodoPlan | null | undefined
): boolean =>
  latestTodoPlan?.anchorId === item.id &&
  isCoveredByComputerUsePlan(items, latestTodoPlan);

// The parent plan can carry work outside the computer-use run; it is only a
// duplicate when the run's own checklist already lists every step.
const isCoveredByComputerUsePlan = (
  items: readonly ChatItem[] | undefined,
  latestTodoPlan: LatestTodoPlan
): boolean => {
  if (!items || latestTodoPlan.state.items.length === 0) return false;

  const anchorIndex = items.findIndex(
    (candidate) => candidate.id === latestTodoPlan.anchorId
  );
  if (anchorIndex < 0) return false;

  for (let index = anchorIndex + 1; index < items.length; index += 1) {
    const candidate = items[index];
    if (!candidate || candidate.kind === 'user_message') break;
    if (candidate.kind === 'tool_use' && candidate.isComputerUse) {
      return isPlanCoveredBy(
        latestTodoPlan.state.items.map((planItem) => planItem.title),
        candidate.toolCallDisplay?.planSteps
      );
    }
  }

  return false;
};

const isPlanCoveredBy = (
  parentSteps: readonly string[],
  computerUseSteps: readonly string[] | undefined
): boolean => {
  if (!computerUseSteps?.length) return false;
  const covered = new Set(computerUseSteps.map(normalizePlanStep));
  return parentSteps.every((step) => covered.has(normalizePlanStep(step)));
};

const normalizePlanStep = (step: string): string =>
  step.replace(/\s+/g, ' ').trim().toLowerCase();

const previousUserMessageText = (
  items: ChatItem[] | undefined,
  item: ChatItem
): string | undefined => {
  if (!items) return undefined;
  const index = items.findIndex((candidate) => candidate.id === item.id);
  if (index <= 0) return undefined;

  for (let i = index - 1; i >= 0; i -= 1) {
    const previous = items[i];
    if (previous?.kind === 'user_message') return previous.text;
  }
  return undefined;
};
